<template>
    <div>
        <div
            class="flex items-center border rounded-md overflow-hidden bg-theme-input"
            :class="invalid ? 'border-theme-danger' : 'border-theme-border'"
        >
            <button
                type="button"
                class="flex items-center gap-1 text-sm text-theme-base bg-theme-hover px-3 py-2 border-r border-theme-border whitespace-nowrap"
                :aria-label="`País: ${selectedCountry.name}. Cambiar país`"
                data-test="country-button"
                @click="pickerOpen = true"
            >
                <span aria-hidden="true">{{ selectedCountry.flag }}</span>
                <span>+{{ selectedCountry.dialCode }}</span>
                <ChevronDownIcon class="w-3 h-3 text-theme-muted" />
            </button>
            <input
                ref="input"
                :value="value"
                type="tel"
                inputmode="numeric"
                :placeholder="placeholder"
                :maxlength="maxLength"
                class="flex-1 min-w-0 outline-none text-sm px-3 py-2 bg-theme-input text-theme-base"
                data-test="number-input"
                @input="onInput"
                @paste="onPaste"
                @blur="$emit('blur')"
            />
        </div>

        <AppDialog v-model="pickerOpen" size="sm" append-to-body>
            <template #header>
                <h2 class="text-lg font-semibold text-theme-base">
                    País de tu WhatsApp
                </h2>
            </template>

            <ul class="flex flex-col gap-1">
                <li
                    v-for="(option, index) in orderedCountries"
                    :key="option.code"
                    :class="
                        index === 1
                            ? 'mt-1 pt-1 border-t border-theme-border'
                            : ''
                    "
                >
                    <button
                        type="button"
                        class="w-full flex items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition hover:bg-theme-hover"
                        :class="
                            option.code === selectedCountry.code
                                ? 'border-theme-primary text-theme-primary font-semibold'
                                : 'border-transparent text-theme-base'
                        "
                        :data-test="`country-option-${option.code}`"
                        @click="selectCountry(option.code)"
                    >
                        <span aria-hidden="true" class="text-lg">
                            {{ option.flag }}
                        </span>
                        <span class="flex-1">{{ option.name }}</span>
                        <span class="text-theme-muted">
                            +{{ option.dialCode }}
                        </span>
                    </button>
                </li>
            </ul>

            <template #footer>
                <button
                    type="button"
                    class="w-full rounded-md border border-theme-primary px-3 py-2 text-sm font-medium text-theme-primary transition hover:bg-theme-hover"
                    data-test="missing-country"
                    @click="requestMissingCountry"
                >
                    Mi país no está — contacta a soporte
                </button>
            </template>
        </AppDialog>
    </div>
</template>

<script>
import {
    DEFAULT_WHATSAPP_COUNTRY,
    WHATSAPP_COUNTRIES,
    buildWhatsappCountryRequestLink,
    getWhatsappCountry,
    getWhatsappLocalMaxLength,
    normalizeWhatsappCountryCode,
    sanitizeWhatsappDigits,
    splitWhatsappNumber
} from "@/common/lib/whatsapp";

import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "WhatsappNumberInput",
    components: { AppDialog, ChevronDownIcon },
    props: {
        // Solo los digitos locales, sin indicativo.
        value: { type: String, default: "" },
        country: { type: String, default: DEFAULT_WHATSAPP_COUNTRY },
        // Pais que se muestra primero en el modal (el del tenant).
        pinnedCountry: { type: String, default: DEFAULT_WHATSAPP_COUNTRY },
        invalid: { type: Boolean, default: false },
        placeholder: { type: String, default: "3001234567" }
    },
    emits: ["input", "update:country", "blur"],
    data: () => ({
        pickerOpen: false
    }),
    computed: {
        selectedCountry() {
            return getWhatsappCountry(this.country);
        },
        maxLength() {
            return getWhatsappLocalMaxLength(this.country);
        },
        orderedCountries() {
            const pinned = normalizeWhatsappCountryCode(this.pinnedCountry);
            return [
                getWhatsappCountry(pinned),
                ...WHATSAPP_COUNTRIES.filter(
                    (country) => country.code !== pinned
                )
            ];
        }
    },
    methods: {
        focus() {
            this.$refs.input.focus();
        },
        onInput(event) {
            const digits = sanitizeWhatsappDigits(event.target.value);
            if (event.target.value !== digits) event.target.value = digits;
            this.$emit("input", digits);
        },
        // Un numero pegado con "+" trae su indicativo: se usa para elegir el
        // pais en vez de dejarlo dentro del numero local.
        onPaste(event) {
            const text = (event.clipboardData?.getData("text") || "").trim();
            if (!text.startsWith("+")) return;
            event.preventDefault();
            const { country, localNumber } = splitWhatsappNumber(
                text,
                this.country
            );
            this.$emit("update:country", country);
            this.$emit("input", localNumber);
        },
        selectCountry(code) {
            this.$emit("update:country", code);
            this.pickerOpen = false;
        },
        requestMissingCountry() {
            window.open(buildWhatsappCountryRequestLink(), "_blank");
        }
    }
};
</script>
